import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { teamAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AppLayout from '../components/AppLayout';
import { toast } from 'sonner';
import {
  Users, Crown, UserPlus, Mail, Loader2, X, LogOut, Sparkles, ArrowRight,
  CheckCircle2, AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { de as deLocale, enUS as enLocale } from 'date-fns/locale';

/**
 * Team membership management. V1.5 per-seat subscription.
 *
 * Three personas funnel into this page:
 *
 *   - SOLO/FREE user: no team yet. Show the "Start a Team" card with a
 *     seat picker + live price preview, button → Stripe Checkout.
 *   - OWNER: full management. Member list, invite form (email →
 *     server-sent invite link), pending-invite list with cancel
 *     buttons, per-row "Entfernen" buttons. Adding/removing members
 *     instantly reflects in Stripe via subscription quantity sync,
 *     with proration.
 *   - MEMBER: read-only view of the team + a "Team verlassen" button.
 *     Properties + videos stay theirs after leaving. Only their tier
 *     drops back to free for new renders.
 *
 * The `/team/checkout` endpoint stashes the desired team name on the
 * user record; the Stripe webhook actually creates the Team doc.
 * That's why a brand-new owner who refreshes too fast can briefly see
 * "no team yet". The polling fallback inside loadTeam handles that
 * race by retrying twice with a 2-second backoff.
 */

const TEAM_BASE_PRICE = 179;
const TEAM_ADDITIONAL_SEAT_PRICE = 89;
const TEAM_INCLUDED_SEATS = 2;

function calcTeamPriceEur(seats) {
  if (seats <= TEAM_INCLUDED_SEATS) return TEAM_BASE_PRICE;
  return TEAM_BASE_PRICE + (seats - TEAM_INCLUDED_SEATS) * TEAM_ADDITIONAL_SEAT_PRICE;
}

export default function TeamPage() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = lang === 'de' ? deLocale : enLocale;

  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [role, setRole] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);

  // Start-a-team form (only used when user has no team yet).
  const [newTeamName, setNewTeamName] = useState('');
  const [newSeats, setNewSeats] = useState(2);
  const [startingCheckout, setStartingCheckout] = useState(false);

  // Invite-form (owner-only).
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const [busyMember, setBusyMember] = useState(null); // user_id we're removing
  const [busyInvite, setBusyInvite] = useState(null); // invitation_id we're cancelling
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    loadTeam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTeam = async () => {
    try {
      const res = await teamAPI.getMyTeam();
      setTeam(res.data?.team || null);
      setRole(res.data?.role || null);
      setMembers(res.data?.members || []);
      setInvites(res.data?.invites || []);
    } catch (e) {
      toast.error(t('team.loadFailed') || 'Team konnte nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  // ── Start-a-team flow (no team yet) ──────────────────────────────
  const handleStartCheckout = async () => {
    setStartingCheckout(true);
    try {
      const res = await teamAPI.startCheckout({
        name: newTeamName.trim() || undefined,
        seats: newSeats,
      });
      // Hard navigation. Stripe Checkout is a separate origin.
      window.location.href = res.data.url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail
        || t('team.checkoutFailed')
        || 'Checkout konnte nicht gestartet werden.'
      );
      setStartingCheckout(false);
    }
  };

  // ── Owner ops ────────────────────────────────────────────────────
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      const res = await teamAPI.invite(inviteEmail.trim());
      if (res.data?.sent) {
        toast.success(t('team.inviteSent') || 'Einladung verschickt.');
      } else {
        toast.warning(
          res.data?.warning
          || t('team.inviteSentNoEmail')
          || 'Einladung erstellt, E-Mail-Versand fehlgeschlagen.'
        );
      }
      setInviteEmail('');
      await loadTeam();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('team.inviteFailed')
        || 'Einladung fehlgeschlagen.'
      );
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvite = async (invitationId) => {
    setBusyInvite(invitationId);
    try {
      await teamAPI.cancelInvite(invitationId);
      await loadTeam();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setBusyInvite(null);
    }
  };

  const handleRemoveMember = async (memberUserId, memberLabel) => {
    // Cheap confirm. Destructive op + Stripe-billing impact, deserves
    // a moment of friction.
    if (!window.confirm(
      t('team.confirmRemove', { who: memberLabel })
      || `${memberLabel} wirklich aus dem Team entfernen?`
    )) return;
    setBusyMember(memberUserId);
    try {
      await teamAPI.removeMember(memberUserId);
      toast.success(t('team.memberRemoved') || 'Mitglied entfernt.');
      await loadTeam();
    } catch (err) {
      toast.error(
        err.response?.data?.detail
        || t('team.removeFailed')
        || 'Entfernen fehlgeschlagen.'
      );
    } finally {
      setBusyMember(null);
    }
  };

  // ── Member ops ───────────────────────────────────────────────────
  const handleLeave = async () => {
    if (!window.confirm(
      t('team.confirmLeave')
      || 'Team wirklich verlassen? Du kannst danach nicht mehr auf Team-Funktionen zugreifen.'
    )) return;
    setLeaving(true);
    try {
      await teamAPI.leave();
      toast.success(t('team.leftTeam') || 'Du hast das Team verlassen.');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
      setLeaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 text-[#1f4d3a] animate-spin" />
        </div>
      </AppLayout>
    );
  }

  // ── No team yet → show start-a-team card ─────────────────────────
  if (!team) {
    const monthly = calcTeamPriceEur(newSeats);
    return (
      <AppLayout>
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-[#23211a] mb-2 flex items-center justify-center gap-3">
              <Users className="w-9 h-9 text-[#1f4d3a]" />
              {t('team.startTitle') || 'Starte ein Team'}
            </h1>
            <p className="text-[#6f6a5c]">
              {t('team.startSubtitle')
                || 'Mehrere Makler unter einer Abrechnung. Günstiger als einzelne Solo-Abos.'}
            </p>
          </div>

          <Card className="glass border-[#1f4d3a]/30">
            <CardHeader>
              <CardTitle>{t('team.startCardTitle') || 'Team-Abo einrichten'}</CardTitle>
              <CardDescription>
                {t('team.startCardSubtitle')
                  || 'Wähle die Anfangs-Anzahl der Plätze. Du kannst jederzeit Mitglieder hinzufügen oder entfernen. Wir rechnen das anteilig auf der nächsten Rechnung ab.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                  {t('team.nameLabel') || 'Team-Name'}
                </label>
                <Input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder={t('team.namePlaceholder') || 'Mustermann Immobilien'}
                  className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                  data-testid="team-name-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                  {t('team.seatsLabel') || 'Plätze (mind. 2)'}
                </label>
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setNewSeats(Math.max(2, newSeats - 1))}
                    disabled={newSeats <= 2}
                    className="border-[#d8d0bd] text-[#4a463b] w-10 h-10 p-0"
                    aria-label={t('team.fewerSeats') || 'Weniger Plätze'}
                  >
                    −
                  </Button>
                  <span
                    className="text-2xl font-semibold text-[#1f4d3a] min-w-[3ch] text-center"
                    data-testid="team-seats-count"
                  >
                    {newSeats}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setNewSeats(newSeats + 1)}
                    className="border-[#d8d0bd] text-[#4a463b] w-10 h-10 p-0"
                    aria-label={t('team.moreSeats') || 'Mehr Plätze'}
                  >
                    +
                  </Button>
                </div>
              </div>

              <div className="rounded-lg border border-[#1f4d3a]/30 bg-[#f0f3ec] p-4">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-[#6f6a5c]">
                    {t('team.priceLabel') || 'Monatlich'}
                  </span>
                  <span className="text-3xl font-bold text-[#23211a]">
                    €{monthly}
                  </span>
                </div>
                <p className="text-xs text-[#8a8474] mt-1">
                  €{TEAM_BASE_PRICE} {t('team.priceBase') || 'für die ersten 2 Plätze'}
                  {newSeats > TEAM_INCLUDED_SEATS && (
                    <> + {newSeats - TEAM_INCLUDED_SEATS} × €{TEAM_ADDITIONAL_SEAT_PRICE}</>
                  )}
                  {' '}– {t('team.priceVsSolo', { solo: newSeats * 99, save: newSeats * 99 - monthly })
                    || `vs. ${newSeats}× Solo (€${newSeats * 99}). Sparst €${newSeats * 99 - monthly}`}
                </p>
              </div>

              <Button
                type="button"
                onClick={handleStartCheckout}
                disabled={startingCheckout}
                className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary"
                data-testid="team-start-checkout"
              >
                {startingCheckout ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                {t('team.startCta') || 'Team-Abo starten'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-[#8a8474] text-center">
                {t('team.checkoutHint')
                  || 'Stripe-Checkout. Sicher abgewickelt, jederzeit kündbar.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // ── Team exists → owner OR member view ───────────────────────────
  const isOwner = role === 'owner';
  const monthlyForCurrentSeats = calcTeamPriceEur(team.seat_count);

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#23211a] flex items-center gap-3">
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-[#1f4d3a]" />
              {team.name || (t('team.defaultName') || 'Mein Team')}
            </h1>
            <p className="text-sm text-[#8a8474] mt-1">
              {t('team.subtitle', { count: team.seat_count })
                || `${team.seat_count} Plätze · €${monthlyForCurrentSeats}/Monat`}
            </p>
          </div>
          {isOwner ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#c7a869]/15 border border-[#c7a869] text-xs font-medium text-yellow-300 self-start">
              <Crown className="w-3.5 h-3.5" />
              {t('team.youAreOwner') || 'Du bist Inhaber'}
            </span>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={handleLeave}
              disabled={leaving}
              className="border-[#b3543f]/50 text-[#a34430] hover:bg-[#f7e8e3] self-start"
              data-testid="team-leave"
            >
              {leaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4 mr-2" />
              )}
              {t('team.leaveCta') || 'Team verlassen'}
            </Button>
          )}
        </div>

        {/* Members list */}
        <Card className="glass border-[#e3dccb] mb-6">
          <CardHeader>
            <CardTitle>{t('team.membersTitle') || 'Mitglieder'}</CardTitle>
            <CardDescription>
              {t('team.membersSubtitle', { count: members.length, max: team.seat_count })
                || `${members.length} von ${team.seat_count} Plätzen belegt`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-[#e3dccb]/60">
              {members.map((m) => (
                <li
                  key={m.id}
                  className="py-3 flex items-center justify-between gap-3"
                  data-testid={`team-member-${m.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[#23211a] truncate flex items-center gap-2">
                      {m.full_name || m.email}
                      {m.team_role === 'owner' && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#c7a869]/15 text-yellow-300 text-[10px]">
                          <Crown className="w-3 h-3" />
                          {t('team.ownerLabel') || 'Inhaber'}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-[#8a8474] truncate">{m.email}</p>
                  </div>
                  {isOwner && m.team_role !== 'owner' && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(m.id, m.full_name || m.email)}
                      disabled={busyMember === m.id}
                      className="border-[#b3543f]/50 text-[#a34430] hover:bg-[#f7e8e3] shrink-0"
                      data-testid={`team-remove-${m.id}`}
                    >
                      {busyMember === m.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5" />
                      )}
                    </Button>
                  )}
                </li>
              ))}
              {members.length === 0 && (
                <li className="py-6 text-center text-sm text-[#8a8474]">
                  {t('team.noMembers') || 'Noch keine Mitglieder.'}
                </li>
              )}
            </ul>
          </CardContent>
        </Card>

        {/* Owner-only: invite form + pending invites */}
        {isOwner && (
          <>
            <Card className="glass border-[#e3dccb] mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-[#1f4d3a]" />
                  {t('team.inviteTitle') || 'Mitglied einladen'}
                </CardTitle>
                <CardDescription>
                  {t('team.inviteSubtitle')
                    || 'E-Mail-Adresse eingeben. Wir schicken eine Einladung. Beim Annehmen wird automatisch ein Platz hinzugefügt (anteilig abgerechnet).'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={handleInvite}
                  className="flex flex-col sm:flex-row gap-2"
                  data-testid="team-invite-form"
                >
                  <Input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder={t('team.invitePlaceholder') || 'kollege@firma.at'}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] flex-1"
                    required
                    data-testid="team-invite-email"
                  />
                  <Button
                    type="submit"
                    disabled={sendingInvite || !inviteEmail.trim()}
                    className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white"
                    data-testid="team-invite-submit"
                  >
                    {sendingInvite ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {t('team.inviteCta') || 'Einladen'}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {invites.length > 0 && (
              <Card className="glass border-[#e3dccb] mb-6">
                <CardHeader>
                  <CardTitle>{t('team.pendingTitle') || 'Offene Einladungen'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="divide-y divide-[#e3dccb]/60">
                    {invites.map((inv) => (
                      <li
                        key={inv.id}
                        className="py-3 flex items-center justify-between gap-3"
                        data-testid={`team-invite-${inv.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-[#23211a] truncate">{inv.invited_email}</p>
                          <p className="text-xs text-[#8a8474]">
                            {t('team.expiresAt', {
                              date: inv.expires_at
                                ? format(new Date(inv.expires_at), 'PPP', { locale: dateLocale })
                                : '',
                            }) || `läuft ab: ${inv.expires_at}`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleCancelInvite(inv.id)}
                          disabled={busyInvite === inv.id}
                          className="border-[#d8d0bd] text-[#4a463b] shrink-0"
                        >
                          {busyInvite === inv.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <>
                              <X className="w-3.5 h-3.5 mr-1" />
                              {t('team.cancelInvite') || 'Widerrufen'}
                            </>
                          )}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Billing pointer. Owners cancel/manage via Stripe portal */}
            <Card className="glass border-[#e3dccb]">
              <CardContent className="p-5">
                <p className="text-sm text-[#6f6a5c] mb-3">
                  <AlertTriangle className="inline w-4 h-4 mr-1.5 -mt-0.5 text-[#9a6d1f]" />
                  {t('team.billingHint')
                    || 'Abrechnung, Zahlungsmethode und Kündigung über das Stripe-Abrechnungsportal. Siehe Pricing-Seite.'}
                </p>
                <Button
                  variant="outline"
                  onClick={() => navigate('/pricing')}
                  className="border-[#d8d0bd] text-[#4a463b]"
                >
                  {t('team.toPricing') || 'Zur Abrechnung'}
                </Button>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
